import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { authorizedFetch, json } from '@nimbitlabs/ui';
import { useAccountStore } from '../account/accountStore';

export interface CreateApiKeyDto {
  name: string;
  expiresAt?: string;
  scopes?: string[];
}

export interface ApiKey {
  id: string;
  name: string;
  key: string;
  createdAt: string;
  expiresAt?: string;
  lastUsedAt?: string;
  scopes?: string[];
}

interface ApiKeysState {
  apiKeys: ApiKey[];
  loading: boolean;
  error: string | null;
  
  fetchApiKeys: () => Promise<void>;
  createApiKey: (data: CreateApiKeyDto) => Promise<ApiKey>;
  revokeApiKey: (id: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

export const useApiKeysStore = create<ApiKeysState>()(
  subscribeWithSelector((set, get) => ({
  apiKeys: [],
  loading: false,
  error: null,
  
  fetchApiKeys: async () => {
    set({ loading: true, error: null });
    try {
      const response = await authorizedFetch('/api-keys');
      const apiKeys = await json<ApiKey[]>(response);
      set({ apiKeys, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch API keys', loading: false });
    }
  },
  
  createApiKey: async (data: CreateApiKeyDto) => {
    try {
      const response = await authorizedFetch('/api-keys', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      const newApiKey = await json<ApiKey>(response);
      set({ apiKeys: [...get().apiKeys, newApiKey] });
      return newApiKey;
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to create API key' });
      throw e;
    }
  },
  
  revokeApiKey: async (id: string) => {
    try {
      const response = await authorizedFetch(`/api-keys/${id}`, {
        method: 'DELETE',
      });
      await json(response);
      set({ apiKeys: get().apiKeys.filter(k => k.id !== id) });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to revoke API key' });
      throw e;
    }
  },
  
  clearError: () => set({ error: null }),
  
  reset: () => set({ apiKeys: [], loading: false, error: null }),
})));

// Subscribe to account changes and auto-fetch data
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      useApiKeysStore.getState().fetchApiKeys();
    } else {
      useApiKeysStore.getState().reset();
    }
  }
);
